import React, { useState, useMemo } from "react";
import CartItemEditModal from "./CartItemEditModal";
import { computeTicketTotals } from "./ticketCalculations";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} €`;

const CashierTicket = ({ 
  cart, 
  updateQuantity,
  updateCartItem,
  removeItem, 
  clearCart, 
  onSuspendCart,
  total, 
  costMap, 
  taxRates, 
  clientName, 
  orderMode, 
  setOrderMode,
  businessUnit = "alimentation",
  onCheckout, 
  onQuickAdd,
  pendingQuickProducts,
  onDismissQuickProduct,
  couponError,
  reduction,
  reductionDiscount,
  onOpenReduction,
  onClearReduction,
  originalTotal,
}) => {
  const [activeNumpadItem, setActiveNumpadItem] = useState(null);

  const tabs = [
    { id: "magasin", label: "EN MAGASIN" },
    { id: "emporter", label: "A EMPORTER" },
    { id: "livrer", label: "A LIVRER" }
  ];

  const totals = useMemo(
    () => computeTicketTotals(cart.items, costMap, taxRates),
    [cart.items, costMap, taxRates]
  );



  const lineCostLabel = (item) => {
    const entry = item.product && item.product.id ? costMap.get(item.product.id) : null;
    const unitCost = entry ? entry.unitCost : (item.product?.purchasePrice || 0);
    return `PA ${Number(unitCost).toFixed(2)}${item.isWeighted ? "/kg" : "/u"}`;
  };

  const totalHT = totals.knownCount > 0 ? Number(total) - totals.vatTotal : 0;
  const realMargin = totals.knownCount > 0 ? totalHT - totals.purchaseTotal : null;

  return (
    <div className="card shadow-sm border-0 h-100 d-flex flex-column" style={{ borderRadius: 0 }}>
      {businessUnit === "traiteur" && (
        <div className="pos-ticket-unit">
          <i className="bi bi-shop me-1"></i> TRAITEUR
        </div>
      )}
      {clientName && (
        <div className="pos-ticket-client">
          <i className="bi bi-person-fill"></i> {clientName}
        </div>
      )}
      {/* Tabs */}
      <div className="d-flex p-2 gap-1 border-bottom" style={{ borderColor: "var(--pos-line)" }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`pos-tab ${orderMode === tab.id ? "is-active" : ""}`}
            onClick={() => setOrderMode(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Ticket Items */}
      <div className="flex-grow-1 overflow-auto p-2">
        {cart.items.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100 pos-empty-ticket">
            Panier vide
          </div>
        ) : (
          <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
            {cart.items.map((item) => (
              <li className="pos-ticket-item shadow-sm d-flex flex-column gap-2 p-2" key={item.id}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={item.product?.imageUrl || "/placeholder.png"}
                      alt={item.title}
                      style={{ width: "30px", height: "30px", objectFit: "cover", borderRadius: "4px" }}
                      onError={(e) => { e.target.src = "https://via.placeholder.com/30"; }}
                    />
                    <span className="fw-bold text-truncate" style={{ maxWidth: "160px", fontSize: "0.9rem" }}>{item.title}</span>
                    {item.isReturn && <span className="badge bg-danger ms-1" style={{ fontSize: "0.65rem" }}>RETOUR</span>}
                    {item.appliedGroupName && <span className="badge bg-info ms-1 text-dark" style={{ fontSize: "0.65rem" }}>{item.appliedGroupName}</span>}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-link text-danger p-0" onClick={() => removeItem(item.id)}>
                      <i className="bi bi-trash fs-5"></i>
                    </button>
                  </div>
                </div>
                {Array.isArray(item.product && item.product.components) && item.product.components.length > 0 && (
                  <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.3, marginTop: "2px" }}>
                    {item.product.components.join(" · ")}
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-end mt-1">
                  <div className="d-flex align-items-center gap-2">
                    <span className="pos-badge-qty">x {item.quantity}</span>
                    <button
                      className="pos-edit-btn"
                      onClick={() => setActiveNumpadItem(item)}
                    >
                      <i className="bi bi-pencil-square"></i> Qte/Poids
                    </button>
                  </div>
                  <div className="text-end">
                    {item.lineDiscountValue > 0 && (
                      <div style={{ fontSize: "0.75rem", color: "#f97316", fontWeight: "bold" }}>
                        Remise : {item.lineDiscountValue}{item.lineDiscountType} 
                        ({item.lineDiscountApplyType === 'all' ? 'x N' : 'x 1'})
                      </div>
                    )}
                    <div className="fw-bold">{formatPrice(item.lineTotal)}</div>
                    <div style={{ fontSize: "0.75rem", color: "#f97316", fontWeight: "bold" }}>{lineCostLabel(item)}</div>
                  </div>
                </div>
                {item.note && (
                  <div className="text-muted mt-1 p-1 bg-light rounded" style={{ fontSize: "0.75rem", fontStyle: "italic", borderLeft: "2px solid #0ea5e9" }}>
                    <i className="bi bi-chat-text me-1"></i>{item.note}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer / Totals */}
      <div className="pos-ticket-footer p-3 mt-auto">
        {pendingQuickProducts && pendingQuickProducts.length > 0 && (
          <div className="mb-2 p-2 rounded-2 d-flex align-items-start gap-2" style={{ backgroundColor: "#fffbeb", borderLeft: "3px solid #f59e0b", fontSize: "0.8rem", color: "#92400e" }}>
            <i className="bi bi-info-circle-fill mt-1" style={{ color: "#f59e0b", flexShrink: 0 }}></i>
            <div className="flex-grow-1">
              <div className="fw-bold mb-1">À compléter :</div>
              {pendingQuickProducts.map((p) => (
                <div key={p.id} className="d-flex justify-content-between align-items-center">
                  <span className="text-truncate" style={{ maxWidth: "180px" }}>{p.name}</span>
                  <button className="btn btn-sm p-0 text-decoration-underline" style={{ color: "#92400e", fontSize: "0.75rem" }} onClick={() => onDismissQuickProduct(p.id)}>
                    OK
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reduction Input */}
        <div className="mb-2">
          {reduction && (reduction.type && reduction.type !== "none") ? (
            <div className="d-flex align-items-center justify-content-between p-2 rounded-2" style={{ backgroundColor: "#d1fae5", border: "1px solid #6ee7b7", fontSize: "0.8rem" }}>
              <div>
                <i className="bi bi-tag-fill me-1" style={{ color: "#16a34a" }}></i>
                <span className="fw-bold" style={{ color: "#065f46" }}>
                  {reduction.type === "coupon"
                    ? reduction.couponCode
                    : reduction.type === "fixed"
                      ? `${reduction.value} €`
                      : `${reduction.value} %`}
                </span>
                <span className="ms-1" style={{ color: "#16a34a" }}>-{formatPrice(reductionDiscount || 0)}</span>
              </div>
              <button className="btn btn-sm p-0" style={{ color: "#ef4444" }} onClick={onClearReduction}>
                <i className="bi bi-x-circle-fill"></i>
              </button>
            </div>
          ) : (
            <button
              className="btn btn-sm w-100 fw-bold"
              style={{ backgroundColor: "#dbeafe", color: "#1e40af", borderRadius: "8px", fontSize: "0.8rem" }}
              onClick={onOpenReduction}
            >
              <i className="bi bi-tag me-1"></i>RÉDUCTION
            </button>
          )}
          {couponError && (
            <div className="mt-1" style={{ fontSize: "0.7rem", color: "#ef4444" }}>{couponError}</div>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center mb-2 px-1" style={{ fontSize: "0.70rem", color: "#9ca3af", whiteSpace: "nowrap" }}>
          <span>Produits: {totals.count}</span>
          <span>Prix d'achat: {formatPrice(totals.purchaseTotal || 0)}</span>
          <span>Tva: {formatPrice(totals.vatTotal || 0)}</span>
          <span className="fw-bold" style={{ color: realMargin >= 0 ? "#10b981" : "#ef4444" }}>
            Marge: {realMargin === null ? "0.00 €" : `${realMargin >= 0 ? "+" : ""}${realMargin.toFixed(2)} €`}
          </span>
        </div>
        {reduction && reduction.type !== "none" && reductionDiscount > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-1 px-1" style={{ fontSize: "0.8rem", color: "#16a34a" }}>
            <span><i className="bi bi-tag-fill me-1"></i>Réduction {reduction.type === "coupon" ? `(${reduction.couponCode})` : reduction.type === "fixed" ? `(${reduction.value} €)` : `(${reduction.value}%)`}</span>
            <span className="fw-bold">-{formatPrice(reductionDiscount)}</span>
          </div>
        )}
        <div className="d-flex gap-2">
          <button className="pos-clear-btn d-flex justify-content-center align-items-center" onClick={clearCart} aria-label="Vider le ticket">
            <i className="bi bi-trash-fill"></i>
          </button>
          <button 
            className="d-flex justify-content-center align-items-center text-white" 
            style={{ width: "48px", height: "48px", borderRadius: "8px", backgroundColor: "#f59e0b", fontSize: "1.2rem", flexShrink: 0, border: "none" }}
            onClick={onSuspendCart} 
            title="Mettre en attente"
          >
            <i className="bi bi-pause-fill"></i>
          </button>
          <button className="pos-pay-btn" onClick={onCheckout}>
            <span>REGLER</span>
            <span>{formatPrice(total)}</span>
          </button>
          <button
            className="d-flex justify-content-center align-items-center fw-bold"
            style={{ width: "48px", height: "48px", borderRadius: "8px", backgroundColor: "#22c55e", color: "white", fontSize: "1.5rem", flexShrink: 0, border: "none" }}
            onClick={onQuickAdd}
            title="Ajout rapide"
          >
            +
          </button>
        </div>
      </div>

      {activeNumpadItem && (
        <CartItemEditModal
          isOpen={true}
          item={activeNumpadItem}
          costMap={costMap}
          onClose={() => setActiveNumpadItem(null)}
          onValidate={(updates) => {
            updateCartItem(activeNumpadItem.id, updates);
            setActiveNumpadItem(null);
          }}
        />
      )}
    </div>
  );
};

export default CashierTicket;
